import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import { format, subDays, startOfMonth, endOfMonth, isSameMonth } from "date-fns";
import { useState } from "react";

// Custom tooltip component to ensure correct colors for revenue chart
const CustomRevenueTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-medium text-gray-900">{`Day ${label}`}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {`${entry.dataKey === 'currentMonth' ? 'Current Month' : 'Previous Month'}: ₹${entry.value?.toLocaleString('en-IN') || 0}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Constant colors for the charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

interface MonthlyTrendData {
  month: string;
  revenue: number;
  patients: number;
}

interface DailyRevenueData {
  day: string;
  currentMonth: number;
  previousMonth: number;
}

interface MonthlyRevenueChartProps {
  monthlyTrend: MonthlyTrendData[];
  dailyRevenueData: DailyRevenueData[];
  serviceRevenue: { serviceName: string; revenue: number; count: number }[];
  presentMonthServiceRevenue: { serviceName: string; revenue: number; count: number }[];
  previousMonthServiceRevenue: { serviceName: string; revenue: number; count: number }[];
}

// Helper function to format currency
const formatCurrency = (value: number | string) => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return `₹${numValue.toLocaleString('en-IN')}`;
};

export function MonthlyRevenueChart({ 
  monthlyTrend, 
  dailyRevenueData, 
  serviceRevenue, 
  presentMonthServiceRevenue, 
  previousMonthServiceRevenue 
}: MonthlyRevenueChartProps) {
  const daysInMonth = dailyRevenueData.length;
  const [selectedMonth, setSelectedMonth] = useState<'present' | 'previous'>('present');
  
  // Get the service revenue data based on selected month
  const currentServiceRevenue = selectedMonth === 'present' ? presentMonthServiceRevenue : previousMonthServiceRevenue;
  const monthLabel = selectedMonth === 'present' ? 'Present Month' : 'Previous Month';
  
  // Check if there's data to display
  if (!monthlyTrend || monthlyTrend.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">📈</div>
        <h3 className="text-lg font-semibold mb-2">No Monthly Revenue Data Available</h3>
        <p className="text-muted-foreground">
          Monthly revenue trends will appear here once you have completed patient consultations and payments.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Move summary to the top */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue Summary</CardTitle>
          <CardDescription>Key metrics from daily revenue data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(dailyRevenueData.reduce((sum, item) => sum + item.currentMonth, 0))}
              </div>
              <div className="text-sm text-muted-foreground">Current Month Revenue</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(dailyRevenueData.reduce((sum, item) => sum + item.previousMonth, 0))}
              </div>
              <div className="text-sm text-muted-foreground">Previous Month Revenue</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {(() => {
                  const currentTotal = dailyRevenueData.reduce((sum, item) => sum + item.currentMonth, 0);
                  const previousTotal = dailyRevenueData.reduce((sum, item) => sum + item.previousMonth, 0);
                  const growth = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : 0;
                  return `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`;
                })()}
              </div>
              <div className="text-sm text-muted-foreground">Growth vs Previous</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Monthly Revenue Trend (Days 1-{daysInMonth})</CardTitle>
            <CardDescription>Daily revenue comparison: Current month vs Previous month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[400px] mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="day" 
                    interval={2} // Show every 3rd day label
                  />
                  <YAxis />
                  <Tooltip content={<CustomRevenueTooltip />} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="currentMonth" 
                    stroke="#0088FE" 
                    strokeWidth={3}
                    name="Current Month Revenue" 
                    dot={{ fill: '#0088FE', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#0088FE', strokeWidth: 2, fill: '#fff' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="previousMonth" 
                    stroke="#FF8042" 
                    strokeWidth={3}
                    name="Previous Month Revenue" 
                    dot={{ fill: '#FF8042', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#FF8042', strokeWidth: 2, fill: '#fff' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Service Types Revenue ({monthLabel})</CardTitle>
                <CardDescription>Revenue distribution by service type from {monthLabel.toLowerCase()}</CardDescription>
              </div>
              <Select value={selectedMonth} onValueChange={(value: 'present' | 'previous') => setSelectedMonth(value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present Month</SelectItem>
                  <SelectItem value="previous">Previous Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[400px] mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={currentServiceRevenue}
                    dataKey="revenue"
                    nameKey="serviceName"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    innerRadius={40}
                  >
                    {currentServiceRevenue.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: number, name: string) => [formatCurrency(value), name]} 
                    labelFormatter={(label) => `Service: ${label}`}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Daily Revenue Bar Chart</CardTitle>
          <CardDescription>Daily revenue amounts with color-coded bars</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[400px] mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyRevenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="day" 
                  interval={2} // Show every 3rd day label
                />
                <YAxis />
                <Tooltip 
                  formatter={(value: number) => [formatCurrency(value), 'Daily Revenue']}
                  labelFormatter={(label) => `Day ${label}`}
                />
                <Legend />
                <Bar 
                  dataKey="currentMonth" 
                  name="Daily Revenue"
                  radius={[4, 4, 0, 0]}
                >
                  {dailyRevenueData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]} 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 